<template>
  <el-dialog
    :visible="visible"
    :title="$tc('tournament.edit_winner')"
    width="500px"
    :before-close="handleClose"
    @close="handleClose"
  >
    <div class="winner-edit-content">
      <div class="winner-section">
        <label class="winner-label">Winner</label>

        <div v-if="selectedWinnerId && selectedWinnerData" class="winner-display">
          <div class="winner-card">
            <span class="winner-name">{{ getCompetitorName(selectedWinnerData) }}</span>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="handleRemoveWinner"
            >
              {{ $tc('general.remove') }}
            </el-button>
          </div>
        </div>

        <el-select
          v-else
          v-model="selectedWinnerId"
          :placeholder="$tc('tournament.choose_winner')"
          class="winner-select"
        >
          <el-option
            v-for="competitor in competitors"
            :key="competitor._id || competitor.id"
            :label="getCompetitorName(competitor)"
            :value="competitor._id || competitor.id"
          />
        </el-select>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $tc('general.cancel') }}</el-button>
      <el-button
        type="primary"
        :loading="submitting"
        @click="handleSave"
      >
        {{ $tc('general.save') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'WinnerEditModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    finalMatch: {
      type: Object,
      default: null
    },
    competitors: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      selectedWinnerId: null,
      submitting: false
    }
  },

  computed: {
    selectedWinnerData () {
      if (!this.selectedWinnerId) return null
      return this.competitors.find(c => (c._id || c.id) === this.selectedWinnerId) || null
    }
  },

  watch: {
    visible (newVal) {
      if (newVal) {
        this.initializeWinner()
      }
    },
    finalMatch: {
      handler () {
        if (this.visible) {
          this.initializeWinner()
        }
      },
      deep: true
    }
  },

  methods: {
    initializeWinner () {
      if (this.finalMatch && this.finalMatch.winnerRegId) {
        this.selectedWinnerId = this.finalMatch.winnerRegId
      } else {
        this.selectedWinnerId = null
      }
    },

    getCompetitorName (competitor) {
      if (!competitor) return 'Unknown'

      if (competitor.isTeam && competitor.teamMembers && competitor.teamMembers.length > 0) {
        const teamMembers = competitor.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          return `${name} (${gender})`
        }).join(', ')
        return teamMembers
      }

      if (!competitor.competitor) return 'Unknown'

      const competitorData = competitor.competitor
      const name = `${competitorData.firstName || ''} ${competitorData.lastName || ''}`.trim()
      const assignedNumber = competitor.assignedNumber ? ` (#${competitor.assignedNumber})` : ''

      return name ? `${name}${assignedNumber}` : 'Unknown'
    },

    handleRemoveWinner () {
      this.selectedWinnerId = null
    },

    handleClose () {
      this.$emit('update:visible', false)
    },

    async handleSave () {
      if (!this.finalMatch) {
        this.$message.error(this.$tc('tournament.no_final_match'))
        return
      }

      // If no winner selected and there was no winner before, just close
      if (!this.selectedWinnerId && !this.finalMatch.winnerRegId) {
        this.handleClose()
        return
      }

      // Check if winner is being changed
      const isChangingWinner = this.finalMatch.winnerRegId &&
                               String(this.finalMatch.winnerRegId) !== String(this.selectedWinnerId)

      const winnerName = this.selectedWinnerId
        ? this.getCompetitorName(this.selectedWinnerData)
        : ''

      let confirmMessage = ''
      if (!this.selectedWinnerId) {
        confirmMessage = 'Da li ste sigurni da želite da uklonite pobednika?'
      } else if (isChangingWinner) {
        confirmMessage = `Da li ste sigurni da želite da promenite pobednika u ${winnerName}?`
      } else {
        confirmMessage = `Potvrdite pobednika: ${winnerName}?`
      }

      try {
        await this.$confirm(
          confirmMessage,
          'Potvrda pobednika',
          {
            confirmButtonText: 'Potvrdi',
            cancelButtonText: 'Otkaži',
            type: 'info'
          }
        )

        this.submitting = true

        await this.$store.dispatch('tournaments/submitMatchResult', {
          matchId: this.finalMatch.id,
          winnerId: this.selectedWinnerId
        })

        this.$message.success('Pobednik je uspešno sačuvan')
        this.$emit('winner-saved')
        this.handleClose()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error saving winner:', error)
          const errorMessage =
            error.response?.data?.error ||
            this.$tc('tournament.save_winner_failed')
          this.$message.error(errorMessage)
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.winner-edit-content {
  padding: 20px 0;
}

.winner-section {
  min-height: 100px;
}

.winner-label {
  display: block;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.winner-display {
  margin-top: 10px;
}

.winner-select {
  width: 100%;
}

.winner-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background-color: #f0f9ff;
  border: 2px solid #67c23a;
  border-radius: 8px;
}

.winner-name {
  font-size: 16px;
  font-weight: bold;
  color: #2e7d32;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
